import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Switch,
  Avatar,
} from '@mui/material';
import {
  Visibility as PreviewIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  DragIndicator as DragIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  Edit as EditIcon,
  Help as HelpIcon,
  AutoAwesome as AiIcon,
  Fullscreen as FullscreenIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import courseService from '../../services/course.service';
import courseContainerService from '../../services/courseContainerService';
import videoService from '../../services/video.service';
import authService from '../../services/auth.service';
import imageService, { Image } from '../../services/image.service';
import ThumbnailPicker from '../../components/ThumbnailPicker';
import { Course, CourseContainer, Video } from '../../types';
import { toast } from 'react-toastify';
import { registerBeforeTabChange, unregisterBeforeTabChange } from './Workshop';
import CourseChoiceContent from '../../components/CourseChoiceContent';
// import CoursePdfViewer from '../../components/CoursePdfViewer';
import SimplePdfViewer from '../../components/SimplePdfViewer';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import PdfDisplay from '../../components/PdfDisplay';
import ParentDropZone from '../../components/ParentDropZone';
import ThumbnailSelector from '../../components/ThumbnailSelector';
import QuickRecapBadge from '../../components/QuickRecapBadge';
import QuickRecapModal from '../../components/QuickRecapModal';
import LatexEditor from '../../components/LatexEditor';

// Sortable Item Component for both courses and containers
interface SortableItemProps {
  item: Course | CourseContainer;
  isContainer?: boolean;
  isSelected: boolean;
  onSelect: (item: Course | CourseContainer, isContainer: boolean) => void;
  onDeleteContainer?: (container: CourseContainer) => void;
  onDeleteCourse?: (course: Course) => void;
  onRenameItem?: (item: Course | CourseContainer, isContainer: boolean) => void;
  onQuickRecapClick?: (course: Course) => void;
  onTogglePublic?: (course: Course, isPublic: boolean) => void;
  showDescription?: boolean;
}

const SortableItem: React.FC<SortableItemProps & { activeItemType: 'container' | 'course' | null }> = ({
  item,
  isContainer = false,
  isSelected,
  onSelect,
  onDeleteContainer,
  onDeleteCourse,
  onRenameItem,
  onQuickRecapClick,
  onTogglePublic,
  showDescription,
  activeItemType
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: `${isContainer ? 'container' : 'course'}-${item.id}` });

  const style = {
    transform: (isContainer && activeItemType === 'course') ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Check if item is public
  const isPublic = !isContainer && 'is_public' in item && item.is_public;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: 'pointer',
        backgroundColor: isSelected
          ? 'action.selected'
          : (isContainer && isOver)
            ? 'primary.light'
            : isPublic
              ? 'rgba(25, 118, 210, 0.05)' // Light blue background for public items
              : 'inherit',
        position: 'relative',
        border: isContainer ? '2px dashed' : '1px solid',
        borderColor: isContainer ? (isOver ? 'primary.dark' : 'primary.main') : 'divider',
        transform: isContainer && isOver ? 'scale(1.02)' : 'scale(1)',
        transition: 'all 0.2s ease',
      }}
      onClick={() => onSelect(item, isContainer)}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <DragIcon sx={{ fontSize: 20, color: 'action.disabled' }} />
          </Box>
          <Box flex="1 1 auto" display="flex" alignItems="center" gap={1} minWidth={0}>
            {isContainer ? (
              (item as any).thumbnail_url ? (
                <Avatar src={(item as any).thumbnail_url} variant="rounded" sx={{ width: 40, height: 40 }} />
              ) : (
                <FolderIcon sx={{ color: 'action.active' }} />
              )
            ) : (
              <FileIcon sx={{ color: 'action.disabled', fontSize: 20 }} />
            )}
            <Box minWidth={0} flex={1} sx={{ py: 0.5 }}>
              <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>{item.title}</Typography>
              {isContainer && 'children_count' in item && (
                <Typography variant="caption" color="textSecondary">
                  {item.children_count} contenants, {item.courses_count} cours
                </Typography>
              )}
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-end" flexShrink={0}>
            {/* Première ligne : QuickRecap et Public/Privé */}
            <Box display="flex" gap={0.5} alignItems="center">
              {!isContainer && onQuickRecapClick && (
                <QuickRecapBadge
                  hasCourse={!!(item as Course).quick_recap_course}
                  hasVideo={!!(item as Course).quick_recap_video}
                  onClick={() => onQuickRecapClick(item as Course)}
                  size="small"
                />
              )}
              {!isContainer && onTogglePublic && (
                <Switch
                  size="small"
                  checked={isPublic}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    onTogglePublic(item as Course, e.target.checked);
                  }}
                />
              )}
            </Box>
            {/* Deuxième ligne : Edit et Delete */}
            <Box display="flex" gap={0.5} alignItems="center">
              {onRenameItem && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRenameItem(item, isContainer);
                  }}
                  sx={{ color: 'action.active' }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {isContainer && onDeleteContainer && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteContainer(item as CourseContainer);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {!isContainer && onDeleteCourse && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteCourse(item as Course);
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
        {!isContainer && 'original_author_name' in item && item.original_author_name && (
          <Typography variant="caption" color="primary" display="block">
            Auteur original: {item.original_author_name}
          </Typography>
        )}
        {showDescription && isContainer && (item as CourseContainer).description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 5 }}>
            {(item as CourseContainer).description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

interface CoursesProps {
  urlContainerId?: number | null;
  urlItemId?: number | null;
  onNavChange?: (containerId: number | null, itemId: number | null, label?: string) => void;
}

const Courses: React.FC<CoursesProps> = ({ urlContainerId, urlItemId, onNavChange }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [containers, setContainers] = useState<CourseContainer[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [currentContainer, setCurrentContainer] = useState<CourseContainer | null>(null);
  const [navigationPath, setNavigationPath] = useState<CourseContainer[]>([]);
  const [courseTitle, setCourseTitle] = useState('');
  const [latexContent, setLatexContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewKey, setPdfPreviewKey] = useState(0);
  const [isPublic, setIsPublic] = useState(false);
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);
  const [createType, setCreateType] = useState<'course' | 'container' | 'existing'>('course');
  const [containerTitle, setContainerTitle] = useState('');
  const [containerDescription, setContainerDescription] = useState('');
  const [editContainerDescription, setEditContainerDescription] = useState('');
  const [editContainerThumbnail, setEditContainerThumbnail] = useState<number | null>(null);
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [newCourseTitle, setNewCourseTitle] = useState('');
  const [activeItemType, setActiveItemType] = useState<'container' | 'course' | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState<{item: Course | CourseContainer, isContainer: boolean} | null>(null);
  const [renameTitle, setRenameTitle] = useState('');
  const [thumbnailSelectorOpen, setThumbnailSelectorOpen] = useState(false);
  const [latexHelpOpen, setLatexHelpOpen] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLatexContent, setAiLatexContent] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPdfKey, setAiPdfKey] = useState(0);
  const [allImages, setAllImages] = useState<Image[]>([]);
  const [imageSelectionDialogOpen, setImageSelectionDialogOpen] = useState(false);
  const [quickRecapModalOpen, setQuickRecapModalOpen] = useState(false);
  const [quickRecapCourse, setQuickRecapCourse] = useState<Course | null>(null);
  const [fullscreenLatexEditorOpen, setFullscreenLatexEditorOpen] = useState(false);
  const [editorWidth, setEditorWidth] = useState(66); // Percentage width for the editor

  // Refs to track current values for saving
  const currentCourseRef = useRef<{
    course: Course | null;
    title: string;
    latex: string;
    tags: string[];
    isPublic: boolean;
  }>({
    course: null,
    title: '',
    latex: '',
    tags: [],
    isPublic: false
  });

  // Update ref whenever values change
  useEffect(() => {
    currentCourseRef.current = {
      course: selectedCourse,
      title: courseTitle,
      latex: latexContent,
      tags,
      isPublic
    };
  }, [selectedCourse, courseTitle, latexContent, tags, isPublic]);

  // Sync from URL (back/forward navigation)
  useEffect(() => {
    const currentId = currentContainer?.id ?? null;
    if (urlContainerId !== undefined && urlContainerId !== currentId) {
      if (urlContainerId === null) {
        setCurrentContainer(null);
        setNavigationPath([]);
      } else {
        // Fetch the container and build path
        const restoreContainer = async () => {
          try {
            const container = await courseContainerService.getContainer(urlContainerId);
            setCurrentContainer(container);
            // Build navigation path
            const path: CourseContainer[] = [];
            let cId: number | null = container.parent ?? null;
            while (cId) {
              const c: CourseContainer = await courseContainerService.getContainer(cId);
              path.unshift(c);
              cId = c.parent ?? null;
            }
            path.push(container);
            setNavigationPath(path);
          } catch (err) {
            console.error('Error restoring container from URL:', err);
          }
        };
        restoreContainer();
      }
    }
  }, [urlContainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore selected course from URL
  useEffect(() => {
    if (urlItemId && (!selectedCourse || selectedCourse.id !== urlItemId)) {
      const restoreCourse = async () => {
        try {
          const course = await courseService.getCourse(urlItemId);
          if (course) {
            setSelectedCourse(course);
            setCourseTitle(course.title);
            setLatexContent(course.latex_content);
            setTags(course.tags || []);
            setIsPublic(course.is_public ?? false);
          }
        } catch (err) {
          console.error('Error restoring course from URL:', err);
        }
      };
      restoreCourse();
    }
  }, [urlItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if current user is the author of the course
  const isUserAuthor = useCallback((course: Course | null): boolean => {
    if (!course) return false;
    const currentUser = authService.getCurrentUser();
    if (!currentUser) return false;

    // User is author if they created it OR they are the original author
    return course.created_by === currentUser.id || course.original_author === currentUser.id;
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const initialLoadDone = useRef(false);

  const loadItems = useCallback(async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);

      if (currentContainer) {
        // Load contents of current container
        const contents = await courseContainerService.getContainerContents(currentContainer.id);
        console.log('Container contents:', contents);
        setContainers(Array.isArray(contents?.containers) ? contents.containers : []);
        setCourses(Array.isArray(contents?.courses) ? contents.courses : []);
      } else {
        // Load root level items
        const [containerData, courseData] = await Promise.all([
          courseContainerService.getContainers(null),
          courseService.getCourses(null)
        ]);

        console.log('Root level data - Containers:', containerData, 'Courses:', courseData);

        // Ensure data is arrays
        const validContainerData = Array.isArray(containerData) ? containerData : [];
        const validCourseData = Array.isArray(courseData) ? courseData : [];

        setContainers(validContainerData);
        setCourses(validCourseData);
      }

      // Load all videos for quick recap selection (regardless of container)
      const videoData = await videoService.getAll();
      setVideos(Array.isArray(videoData) ? videoData.filter(v => !v.is_deleted) : []);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      toast.error('Erreur lors du chargement des éléments');
      setCourses([]);
      setContainers([]);
      setVideos([]);
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [currentContainer]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Register callback to save before tab change
  useEffect(() => {
    const saveBeforeTabChange = async () => {
      const current = currentCourseRef.current;
      if (current.course) {
        // Check if user is author
        const currentUser = authService.getCurrentUser();
        const isAuthor = currentUser && (
          current.course.created_by === currentUser.id ||
          current.course.original_author === currentUser.id
        );

        // Save if not public OR if user is author
        if ((!current.course.is_public || isAuthor) && (
            current.title !== current.course.title ||
            current.latex !== current.course.latex_content ||
            JSON.stringify(current.tags) !== JSON.stringify(current.course.tags || []) ||
            current.isPublic !== current.course.is_public
        )) {
          const courseData = {
            title: current.title || 'Nouveau Cours',
            latex_content: current.latex,
            tags: current.tags,
            is_public: current.isPublic,
          };

          try {
            await courseService.updateCourse(current.course.id, courseData);
            console.log('Course saved before tab change');
          } catch (error) {
            console.error('Error saving course before tab change:', error);
          }
        }
      }
    };

    registerBeforeTabChange('courses', saveBeforeTabChange);

    return () => {
      unregisterBeforeTabChange('courses');
    };
  }, []); // Empty dependency array - only register/unregister on mount/unmount

  // Save on component unmount (for navigation outside Workshop)
  useEffect(() => {
    return () => {
      const current = currentCourseRef.current;
      if (current.course) {
        // Check if user is author
        const currentUser = authService.getCurrentUser();
        const isAuthor = currentUser && (
          current.course.created_by === currentUser.id ||
          current.course.original_author === currentUser.id
        );

        // Check for changes and save if not public OR if user is author
        if ((!current.course.is_public || isAuthor) && (
            current.title !== (current.course.title || '') ||
            current.latex !== (current.course.latex_content || '') ||
            JSON.stringify(current.tags) !== JSON.stringify(current.course.tags || []) ||
            current.isPublic !== current.course.is_public
        )) {
          // Fire off the save request without awaiting
          const courseData = {
            title: current.title || 'Nouveau Cours',
            latex_content: current.latex,
            tags: current.tags,
            is_public: current.isPublic,
          };
          courseService.updateCourse(current.course.id, courseData)
            .then(() => console.log('Course saved on unmount'))
            .catch(err => console.error('Error saving course on unmount:', err));
        }
      }
    };
  }, []); // Empty dependency array - only run on unmount

  // Removed auto-refresh PDF preview - SimplePdfViewer handles it with debounce

  const handleDropToParent = async (itemId: string) => {
    const parentId = navigationPath.length > 1
      ? navigationPath[navigationPath.length - 2].id
      : null;

    if (itemId.startsWith('course-')) {
      const courseId = parseInt(itemId.replace('course-', ''));
      const course = courses.find(c => c.id === courseId);
      if (course) {
        try {
          // For public courses, only update container
          const updateData = course.is_public
            ? { container: parentId }
            : { ...course, container: parentId };

          await courseService.updateCourse(courseId, updateData);
          toast.success(`Cours "${course.title}" déplacé vers le niveau parent`);
          await loadItems();
        } catch (error) {
          toast.error('Erreur lors du déplacement');
        }
      }
    } else if (itemId.startsWith('container-')) {
      const containerId = parseInt(itemId.replace('container-', ''));
      const container = containers.find(c => c.id === containerId);
      if (container) {
        try {
          await courseContainerService.updateContainer(containerId, {
            ...container,
            parent: parentId
          });
          toast.success(`Contenant "${container.title}" déplacé vers le niveau parent`);
          await loadItems();
        } catch (error) {
          toast.error('Erreur lors du déplacement du contenant');
        }
      }
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = event.active.id.toString();
    if (activeId.startsWith('container-')) {
      setActiveItemType('container');
    } else if (activeId.startsWith('course-')) {
      setActiveItemType('course');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveItemType(null);
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const activeId = active.id.toString();
      const overId = over.id.toString();

      const isActiveContainer = activeId.startsWith('container-');
      const isOverContainer = overId.startsWith('container-');
      const isActiveCourse = activeId.startsWith('course-');
      const isOverParent = overId === 'parent-folder';

      // Case 1: Dropping onto parent folder (..) zone
      if (isOverParent) {
        await handleDropToParent(activeId);
        return;
      }

      // Case 2: Container dropped on another container
      // If both are containers but not the same, check if we should move inside or reorder
      if (isActiveContainer && isOverContainer) {
        const activeContainerId = parseInt(activeId.replace('container-', ''));
        const overContainerId = parseInt(overId.replace('container-', ''));

        // Prevent dropping a container into itself
        if (activeContainerId === overContainerId) {
          return;
        }

        // For container-to-container, let the regular reordering handle it
        // The visual feedback will show whether it's being inserted before/after
        // Fall through to Case 4 for reordering
      }

      // Case 3: Dropping a course onto a container - move course into container
      if (isActiveCourse && isOverContainer) {
        const courseId = parseInt(activeId.replace('course-', ''));
        const containerId = parseInt(overId.replace('container-', ''));

        const course = courses.find(c => c.id === courseId);
        if (course) {
          try {
            // For public courses, only update container
            const updateData = course.is_public
              ? { container: containerId }
              : { ...course, container: containerId };

            await courseService.updateCourse(courseId, updateData);

            toast.success(`Cours "${course.title}" déplacé dans le contenant`);

            // Reload items to reflect the change
            await loadItems();
          } catch (error) {
            console.error('Error moving course to container:', error);
            toast.error('Erreur lors du déplacement du cours');
          }
        }
        return;
      }

      // Case 4: Regular reordering within the same level
      const allItems = [...containers, ...courses];
      const activeIndex = allItems.findIndex(item => {
        if (activeId.startsWith('container-')) {
          return 'children_count' in item && item.id === parseInt(activeId.replace('container-', ''));
        }
        return 'latex_content' in item && item.id === parseInt(activeId.replace('course-', ''));
      });
      const overIndex = allItems.findIndex(item => {
        if (overId.startsWith('container-')) {
          return 'children_count' in item && item.id === parseInt(overId.replace('container-', ''));
        }
        return 'latex_content' in item && item.id === parseInt(overId.replace('course-', ''));
      });

      if (activeIndex !== -1 && overIndex !== -1) {
        const newItems = arrayMove(allItems, activeIndex, overIndex);

        // Separate containers and courses
        const newContainers = newItems.filter(item => 'children_count' in item) as CourseContainer[];
        const newCourses = newItems.filter(item => 'latex_content' in item) as Course[];

        setContainers(newContainers);
        setCourses(newCourses);

        // Update order values for both types
        const containerOrder = newContainers.map((item, index) => ({ id: item.id, order: index }));
        const courseOrder = newCourses.map((item, index) => ({ id: item.id, order: index + newContainers.length }));

        // Save new order to backend
        if (containerOrder.length > 0) {
          courseContainerService.reorderContainers(containerOrder).catch(error => {
            console.error('Error reordering containers:', error);
            loadItems();
          });
        }
        if (courseOrder.length > 0) {
          courseService.reorderCourses(courseOrder).catch(error => {
            console.error('Error reordering courses:', error);
            loadItems();
          });
        }
      }
    }
  };

  const handleItemSelect = async (item: Course | CourseContainer, isContainer: boolean) => {
    // Save current course before switching if there are REAL changes (if not public OR if user is author)
    if (selectedCourse && (!selectedCourse.is_public || isUserAuthor(selectedCourse)) && (
        courseTitle !== selectedCourse.title ||
        latexContent !== selectedCourse.latex_content ||
        JSON.stringify(tags) !== JSON.stringify(selectedCourse.tags || []) ||
        isPublic !== selectedCourse.is_public
    )) {
      try {
        const courseData = {
          title: courseTitle || 'Nouveau Cours',
          latex_content: latexContent,
          tags,
          is_public: isPublic,
        };
        const updatedCourse = await courseService.updateCourse(selectedCourse.id, courseData);

        // Update course in the list only (don't update selectedCourse to avoid race condition)
        setCourses(prevCourses => prevCourses.map(c =>
          c.id === selectedCourse.id ? { ...c, ...updatedCourse } : c
        ));

        console.log('Saved course before switching');
      } catch (error) {
        console.error('Error saving course:', error);
      }
    }

    if (isContainer) {
      const container = item as CourseContainer;
      setNavigationPath([...navigationPath, container]);
      setCurrentContainer(container);
      onNavChange?.(container.id, null, container.title);
      setSelectedCourse(null);
      setCourseTitle('');
      setLatexContent('');
      setTags([]);
      setIsPublic(false);
      setShowPdfPreview(false);
    } else {
      const course = item as Course;

      // Reload the course from server to get latest data
      try {
        const freshCourse = await courseService.getCourse(course.id);
        if (freshCourse) {
          setSelectedCourse(freshCourse);
          setCourseTitle(freshCourse.title);
          setLatexContent(freshCourse.latex_content);
          setTags(freshCourse.tags || []);
          setIsPublic(freshCourse.is_public ?? false);
          onNavChange?.(currentContainer?.id ?? null, freshCourse.id, freshCourse.title);
        } else {
          // Fallback to cached data if fetch fails
          setSelectedCourse(course);
          setCourseTitle(course.title);
          setLatexContent(course.latex_content);
          setTags(course.tags || []);
          setIsPublic(course.is_public ?? false);
          onNavChange?.(currentContainer?.id ?? null, course.id, course.title);
        }
      } catch (error) {
        console.error('Error fetching fresh course data:', error);
        // Fallback to cached data
        setSelectedCourse(course);
        setCourseTitle(course.title);
        setLatexContent(course.latex_content);
        setTags(course.tags || []);
        setIsPublic(course.is_public ?? false);
        onNavChange?.(currentContainer?.id ?? null, course.id, course.title);
      }

      setShowPdfPreview(false);
    }
  };

  const handleNavigateBack = async () => {
    // Save current course before navigating back if there are REAL changes (if not public OR if user is author)
    if (selectedCourse && (!selectedCourse.is_public || isUserAuthor(selectedCourse)) && (
        courseTitle !== selectedCourse.title ||
        latexContent !== selectedCourse.latex_content ||
        JSON.stringify(tags) !== JSON.stringify(selectedCourse.tags || []) ||
        isPublic !== selectedCourse.is_public
    )) {
      try {
        const courseData = {
          title: courseTitle || 'Nouveau Cours',
          latex_content: latexContent,
          tags,
          is_public: isPublic,
        };
        const updatedCourse = await courseService.updateCourse(selectedCourse.id, courseData);

        // Update selected course with saved values
        setSelectedCourse({
          ...selectedCourse,
          ...updatedCourse
        });

        // Update course in the list
        setCourses(prevCourses => prevCourses.map(c =>
          c.id === selectedCourse.id ? { ...c, ...updatedCourse } : c
        ));

        console.log('Saved course before navigating back');
      } catch (error) {
        console.error('Error saving course:', error);
      }
    }

    if (navigationPath.length > 0) {
      const newPath = [...navigationPath];
      newPath.pop();
      setNavigationPath(newPath);
      const newContainer = newPath[newPath.length - 1] || null;
      setCurrentContainer(newContainer);
      onNavChange?.(newContainer?.id ?? null, null, newContainer?.title);
    }
  };

  const handleRenameItem = async (item: Course | CourseContainer, isContainer: boolean) => {
    setItemToRename({ item, isContainer });
    setRenameTitle(item.title);
    if (isContainer) {
      setEditContainerDescription((item as CourseContainer).description || '');
      setEditContainerThumbnail((item as CourseContainer).thumbnail || null);
    }
    setRenameDialogOpen(true);
  };

  const handleTogglePublic = async (course: Course, newIsPublic: boolean) => {
    try {
      await courseService.updateCourse(course.id, {
        is_public: newIsPublic
      });

      toast.success(`Cours rendu ${newIsPublic ? 'public' : 'privé'}`);

      // Update in the list
      setCourses(courses.map(c => c.id === course.id ? { ...c, is_public: newIsPublic } : c));

      // Update selected course if it's the same
      if (selectedCourse?.id === course.id) {
        setSelectedCourse({ ...selectedCourse, is_public: newIsPublic });
        setIsPublic(newIsPublic);
      }
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const handleQuickRecapClick = (course: Course) => {
    setQuickRecapCourse(course);
    setQuickRecapModalOpen(true);
  };

  const handleSaveQuickRecapCourse = async (courseId: number | null) => {
    if (!quickRecapCourse) return;

    await courseService.updateCourse(quickRecapCourse.id, {
      quick_recap_course: courseId
    });

    toast.success('Référence cours mise à jour');

    // Update the course in the list
    setQuickRecapCourse({
      ...quickRecapCourse,
      quick_recap_course: courseId,
      quick_recap_course_title: courses.find(c => c.id === courseId)?.title || null
    });

    // If it's the selected course, update it too
    if (selectedCourse?.id === quickRecapCourse.id) {
      setSelectedCourse({
        ...selectedCourse,
        quick_recap_course: courseId,
        quick_recap_course_title: courses.find(c => c.id === courseId)?.title || null
      });
    }

    loadItems();
  };

  const handleSaveQuickRecapVideo = async (videoId: number | null) => {
    if (!quickRecapCourse) return;

    await courseService.updateCourse(quickRecapCourse.id, {
      quick_recap_video: videoId
    });

    toast.success('Référence vidéo mise à jour');

    // Update the course in the list
    setQuickRecapCourse({
      ...quickRecapCourse,
      quick_recap_video: videoId,
      quick_recap_video_title: videos.find(v => v.id === videoId)?.title || null
    });

    // If it's the selected course, update it too
    if (selectedCourse?.id === quickRecapCourse.id) {
      setSelectedCourse({
        ...selectedCourse,
        quick_recap_video: videoId,
        quick_recap_video_title: videos.find(v => v.id === videoId)?.title || null
      });
    }

    loadItems();
  };

  const handleConfirmRename = async () => {
    if (!itemToRename || !renameTitle.trim()) return;

    try {
      if (!initialLoadDone.current) setLoading(true);

      if (itemToRename.isContainer) {
        await courseContainerService.updateContainer(itemToRename.item.id, {
          title: renameTitle.trim(),
          description: editContainerDescription,
          thumbnail: editContainerThumbnail,
        } as any);
      } else {
        await courseService.updateCourse(itemToRename.item.id, {
          title: renameTitle.trim()
        });
      }

      toast.success('Renommage effectué avec succès');
      loadItems();
      setRenameDialogOpen(false);
      setItemToRename(null);
      setRenameTitle('');
      setEditContainerDescription('');
    } catch (error: any) {
      toast.error('Erreur lors du renommage: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContainer = async (container: CourseContainer) => {
    const confirmDelete = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le contenant "${container.title}" ?\n` +
      `Son contenu (${container.children_count} contenants et ${container.courses_count} cours) sera déplacé vers le niveau parent.`
    );

    if (!confirmDelete) return;

    try {
      if (!initialLoadDone.current) setLoading(true);

      // Get parent ID (either current container's parent or null for root)
      const parentId = currentContainer?.id || null;

      // First, get the container's contents
      const contents = await courseContainerService.getContainerContents(container.id);

      // Move all child containers to parent level
      if (contents.containers && contents.containers.length > 0) {
        for (const childContainer of contents.containers) {
          await courseContainerService.updateContainer(childContainer.id, {
            ...childContainer,
            parent: parentId
          });
        }
      }

      // Move all courses to parent level
      if (contents.courses && contents.courses.length > 0) {
        for (const course of contents.courses) {
          // For public courses, only update container
          const updateData = course.is_public
            ? { container: parentId }
            : { ...course, container: parentId };

          await courseService.updateCourse(course.id, updateData);
        }
      }

      // Delete the container
      await courseContainerService.deleteContainer(container.id);

      toast.success(`Contenant "${container.title}" supprimé. Son contenu a été déplacé vers le niveau parent.`);

      // Reload items
      await loadItems();
    } catch (error) {
      console.error('Error deleting container:', error);
      toast.error('Erreur lors de la suppression du contenant');
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    // Show a dialog to choose between course and container
    setCreateType('course'); // Default to course
    setContainerTitle('');
    setContainerDescription('');
    setCourseDialogOpen(true);
  };

  const handleCreateContainer = async () => {
    if (!containerTitle.trim()) {
      toast.error('Veuillez entrer un titre pour le contenant');
      return;
    }

    try {
      if (!initialLoadDone.current) setLoading(true);
      await courseContainerService.createContainer({
        title: containerTitle,
        description: containerDescription,
        parent: currentContainer?.id || null
      });
      toast.success('Contenant créé avec succès');
      setContainerTitle('');
      setContainerDescription('');
      setCourseDialogOpen(false);
      await loadItems();
    } catch (error) {
      toast.error('Erreur lors de la création du contenant');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewCourse = async () => {
    console.log('handleCreateNewCourse called with title:', newCourseTitle);
    if (!newCourseTitle.trim()) {
      toast.error('Veuillez entrer un titre pour le cours');
      return;
    }

    try {
      const courseData = {
        title: newCourseTitle,
        latex_content: '', // Le champ accepte maintenant les chaînes vides
        tags: [],
        is_public: false,
        container: currentContainer?.id || null,
      };

      console.log('Creating course with data:', courseData);
      const newCourse = await courseService.createCourse(courseData);
      console.log('Course created:', newCourse);
      toast.success('Cours créé avec succès');

      setCourseDialogOpen(false);
      setNewCourseTitle('');
      setCreateType('course');

      await loadItems();

      if (newCourse) {
        handleItemSelect(newCourse, false);
      }
    } catch (error) {
      console.error('Error creating course:', error);
      toast.error('Erreur lors de la création du cours');
    }
  };

  const handleCourseChoiceSelect = async (course: Course) => {
    await loadItems();
    handleItemSelect(course, false);
  };

  const handlePreview = async () => {
    if (!selectedCourse) return;

    // Save the course first to ensure the PDF will be persistent
    try {
      if (!initialLoadDone.current) setLoading(true);

      const courseData = {
        title: courseTitle || 'Nouveau Cours',
        latex_content: latexContent,
        tags,
        is_public: isPublic,
      };

      const updatedCourse = await courseService.updateCourse(selectedCourse.id, courseData);

      if (updatedCourse) {
        setSelectedCourse({
          ...selectedCourse,
          ...updatedCourse,
        });
      }

      // Now show the preview
      setShowPdfPreview(true);
      setPdfPreviewKey(prev => prev + 1); // Force component remount
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde avant prévisualisation');
      console.error('Error saving before preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleDeleteCourse = (course?: Course) => {
    const courseToDelete = course || selectedCourse;
    if (!courseToDelete) return;
    setCourseToDelete(courseToDelete);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async (keepPublic: boolean) => {
    if (!courseToDelete) return;

    try {
      await courseService.deleteCourse(courseToDelete.id, keepPublic);
      toast.success('Cours supprimé avec succès');

      // If the deleted course was the selected one, clear the selection
      if (selectedCourse?.id === courseToDelete.id) {
        setSelectedCourse(null);
        setCourseTitle('');
        setLatexContent('');
        setTags([]);
        setIsPublic(false);
        setShowPdfPreview(false);
      }

      setCourseToDelete(null);
      setDeleteDialogOpen(false);
      await loadItems();
    } catch (error) {
      toast.error('Erreur lors de la suppression du cours');
    }
  };

  const handleDuplicateCourse = async () => {
    if (!selectedCourse) return;

    try {
      if (!initialLoadDone.current) setLoading(true);
      const duplicatedCourse = await courseService.duplicateCourse(selectedCourse.id);
      toast.success('Cours dupliqué avec succès');
      await loadItems();
      handleItemSelect(duplicatedCourse, false);
    } catch (error) {
      toast.error('Erreur lors de la duplication du cours');
    } finally {
      setLoading(false);
    }
  };

  const handleSetThumbnail = async (selection: { page: number; x: number; y: number; width: number; height: number }) => {
    if (!selectedCourse) return;

    try {
      if (!initialLoadDone.current) setLoading(true);
      const response = await courseService.setThumbnail(selectedCourse.id, selection);

      if (response.success) {
        toast.success('Miniature définie avec succès');

        // Update the selected course with new thumbnail URL
        const updatedCourse = await courseService.getCourse(selectedCourse.id);
        if (updatedCourse) {
          setSelectedCourse(updatedCourse);
        }

        await loadItems();
      } else {
        toast.error(response.error || 'Erreur lors de la définition de la miniature');
      }
    } catch (error: any) {
      console.error('Error setting thumbnail:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la définition de la miniature');
    } finally {
      setLoading(false);
      setThumbnailSelectorOpen(false);
    }
  };

  const loadAllImages = async () => {
    try {
      const images = await imageService.getAll({});
      setAllImages(images);
    } catch (error) {
      console.error('Error loading images:', error);
      toast.error('Erreur lors du chargement des images');
    }
  };

  const handleOpenImageSelection = () => {
    loadAllImages();
    setImageSelectionDialogOpen(true);
  };

  const handleAddLibraryImage = async (imageId: number) => {
    if (!selectedCourse) return;

    try {
      const updatedCourse = await courseService.addLibraryImage(selectedCourse.id, imageId);
      setSelectedCourse(updatedCourse);
      await loadItems();
      toast.success('Image ajoutée au cours');
    } catch (error: any) {
      console.error('Error adding image:', error);
      toast.error('Erreur lors de l\'ajout de l\'image');
    }
  };

  const handleRemoveLibraryImage = async (imageId: number) => {
    if (!selectedCourse) return;

    try {
      const updatedCourse = await courseService.removeLibraryImage(selectedCourse.id, imageId);
      setSelectedCourse(updatedCourse);
      await loadItems();
      toast.success('Image retirée du cours');
    } catch (error: any) {
      console.error('Error removing image:', error);
      toast.error('Erreur lors du retrait de l\'image');
    }
  };

  const handleCopyImageTag = async (index: number) => {
    const imageTag = `\\includegraphics[width=3in]{&balise_image_${index + 1}}`;
    try {
      await navigator.clipboard.writeText(imageTag);
      toast.success(`Commande LaTeX copiée : ${imageTag}`);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Erreur lors de la copie dans le presse-papier');
    }
  };

  const handleAiAssist = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Veuillez entrer un prompt');
      return;
    }

    try {
      setAiLoading(true);

      // Construire le contexte complet
      const fullPrompt = `Je travaille sur du code LaTeX pour créer un document pédagogique.

Voici le code LaTeX actuel:
\`\`\`latex
${aiLatexContent}
\`\`\`

${aiPrompt}

Réponds uniquement avec le code LaTeX modifié, sans explications supplémentaires. Le code doit être prêt à être compilé.`;

      const response = await courseService.assistWithAi(fullPrompt);

      if (response.content) {
        // Nettoyer la réponse pour ne garder que le code LaTeX
        let cleanedContent = response.content;

        // Supprimer les balises markdown si présentes
        cleanedContent = cleanedContent.replace(/```latex\n?/g, '').replace(/```\n?/g, '');

        setAiLatexContent(cleanedContent);
        toast.success('Contenu généré avec succès');

        // Régénérer le PDF
        setAiPdfKey(prev => prev + 1);
      } else {
        toast.error('Aucune réponse de l\'IA');
      }
    } catch (error: any) {
      console.error('Error with AI assist:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la génération');
    } finally {
      setAiLoading(false);
    }
  };

  const handleRetryWithAi = async (failedLatexContent: string, errorLogs: string) => {
    try {
      // Open AI assistant with the failed content and error logs
      setAiLatexContent(failedLatexContent);
      setAiAssistantOpen(true);

      // Automatically generate a fix prompt based on the error logs
      const fixPrompt = `Le code LaTeX suivant a échoué à compiler. Voici les logs d'erreur de compilation :

\`\`\`
${errorLogs}
\`\`\`

Analyse les erreurs et corrige le code LaTeX pour qu'il compile correctement. Réponds uniquement avec le code LaTeX corrigé, sans explications.`;

      setAiPrompt(fixPrompt);

      // Automatically trigger AI generation
      setAiLoading(true);

      const fullPrompt = `Je travaille sur du code LaTeX pour créer un document pédagogique.

Voici le code LaTeX actuel:
\`\`\`latex
${failedLatexContent}
\`\`\`

${fixPrompt}

Réponds uniquement avec le code LaTeX modifié, sans explications supplémentaires. Le code doit être prêt à être compilé.`;

      const response = await courseService.assistWithAi(fullPrompt);

      if (response.content) {
        let cleanedContent = response.content;
        cleanedContent = cleanedContent.replace(/```latex\n?/g, '').replace(/```\n?/g, '');

        setAiLatexContent(cleanedContent);
        toast.success('Code LaTeX corrigé généré avec succès');

        // Regenerate PDF preview
        setAiPdfKey(prev => prev + 1);
      } else {
        toast.error('Aucune réponse de l\'IA');
      }
    } catch (error: any) {
      console.error('Error with AI fix:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la correction automatique');
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplyAiContent = () => {
    setLatexContent(aiLatexContent);
    setAiAssistantOpen(false);
    toast.success('Contenu appliqué au cours');
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - Course List */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {currentContainer ? currentContainer.title : 'Cours'}
              </Typography>
              <Button
                startIcon={<AddIcon />}
                size="small"
                onClick={handleNew}
              >
                Nouveau
              </Button>
            </Box>
            <Box display="flex" alignItems="center" mb={1}>
              <Switch size="small" checked={showDescriptions} onChange={(e) => setShowDescriptions(e.target.checked)} />
              <Typography variant="caption" color="text.secondary">Descriptions</Typography>
            </Box>

            {loading ? (
              <CircularProgress />
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={[
                    ...(currentContainer ? ['parent-folder'] : []),
                    ...(Array.isArray(containers) ? containers.map(c => `container-${c.id}`) : []),
                    ...(Array.isArray(courses) ? courses.map(c => `course-${c.id}`) : [])
                  ]}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={1}>
                    {currentContainer && (
                      <ParentDropZone
                        parentContainer={navigationPath[navigationPath.length - 2] || null}
                        onDrop={handleDropToParent}
                        onClick={handleNavigateBack}
                      />
                    )}
                    {Array.isArray(containers) && containers.map((container) => (
                      <SortableItem
                        key={`container-${container.id}`}
                        item={container}
                        isContainer={true}
                        isSelected={false}
                        onSelect={handleItemSelect}
                        onDeleteContainer={handleDeleteContainer}
                        onRenameItem={handleRenameItem}
                        showDescription={showDescriptions}
                        activeItemType={activeItemType}
                      />
                    ))}
                    {Array.isArray(courses) && courses.map((course) => (
                      <SortableItem
                        key={`course-${course.id}`}
                        item={course}
                        isContainer={false}
                        isSelected={selectedCourse?.id === course.id}
                        onSelect={handleItemSelect}
                        onDeleteCourse={handleDeleteCourse}
                        onRenameItem={handleRenameItem}
                        onQuickRecapClick={handleQuickRecapClick}
                        onTogglePublic={handleTogglePublic}
                        activeItemType={activeItemType}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </DndContext>
            )}
          </Paper>
        </Grid>

        {/* Right Panel - Editor */}
        <Grid item xs={12} md={9}>
          <Paper sx={{
            p: 3,
            backgroundColor: selectedCourse?.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
            borderRadius: 2
          }}>
            {!selectedCourse ? (
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                justifyContent="center" 
                minHeight="400px"
                color="text.secondary"
              >
                <Typography variant="h6" gutterBottom>
                  Aucun cours sélectionné
                </Typography>
                <Typography variant="body2">
                  Sélectionnez un cours dans la liste ou créez-en un nouveau
                </Typography>
              </Box>
            ) : (
            <Grid container spacing={2}>
              {/* Champ LaTeX à gauche */}
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Contenu LaTeX</Typography>
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<FullscreenIcon />}
                      onClick={() => setFullscreenLatexEditorOpen(true)}
                      variant="outlined"
                      disabled={!selectedCourse || (selectedCourse.is_public && !isUserAuthor(selectedCourse))}
                    >
                      Plein écran
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AiIcon />}
                      onClick={() => {
                        setAiLatexContent(latexContent);
                        setAiPrompt('');
                        setAiAssistantOpen(true);
                      }}
                      variant="contained"
                      color="secondary"
                      disabled={!selectedCourse}
                    >
                      Assistant IA
                    </Button>
                    <Button
                      size="small"
                      startIcon={<HelpIcon />}
                      onClick={() => setLatexHelpOpen(true)}
                      variant="outlined"
                    >
                      Aide LaTeX
                    </Button>
                  </Box>
                </Box>
                <LatexEditor
                  value={latexContent}
                  onChange={(newValue) => {
                    // Allow editing if user is author
                    if (selectedCourse && (!selectedCourse.is_public || isUserAuthor(selectedCourse))) {
                      setLatexContent(newValue);
                    }
                  }}
                  helperText="Écrivez votre cours en LaTeX"
                  height={600}
                  disabled={!selectedCourse || (selectedCourse.is_public && !isUserAuthor(selectedCourse))}
                />
              </Grid>

              {/* Aperçu PDF à droite */}
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Aperçu PDF</Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PreviewIcon />}
                    onClick={handlePreview}
                    disabled={!selectedCourse}
                  >
                    Prévisualiser
                  </Button>
                </Box>
                {showPdfPreview && selectedCourse ? (
                  <Box sx={{ height: '575px' }}>
                    {selectedCourse?.preview_pdf_url && (
                      <Box display="flex" justifyContent="flex-end" mb={1}>
                        <Button
                          size="small"
                          onClick={() => setShowPdfPreview(false)}
                        >
                          PDF enregistré
                        </Button>
                      </Box>
                    )}
                    <Paper variant="outlined" sx={{ p: 1, height: selectedCourse?.preview_pdf_url ? 'calc(100% - 36px)' : '100%' }}>
                      <SimplePdfViewer
                        key={pdfPreviewKey}
                        courseId={selectedCourse.id}
                        latexContent={latexContent}
                        onPdfGenerated={async () => {
                          // PDF generated successfully - reload course to get preview_pdf_url
                          console.log('PDF generated successfully for course:', selectedCourse.id);
                          try {
                            const updatedCourse = await courseService.getCourse(selectedCourse.id);
                            if (updatedCourse) {
                              setSelectedCourse(updatedCourse);
                            }
                          } catch (error) {
                            console.error('Error reloading course after PDF generation:', error);
                          }
                        }}
                      />
                    </Paper>
                  </Box>
                ) : selectedCourse?.preview_pdf_url ? (
                  <Box sx={{ height: '575px' }}>
                    <Paper variant="outlined" sx={{ p: 1, height: '100%', overflow: 'auto' }}>
                      <PdfDisplay
                        url={selectedCourse.preview_pdf_url}
                        title="Aperçu du cours"
                      />
                    </Paper>
                    {selectedCourse.is_public && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Note : Les nouvelles visualisations ne modifieront pas le PDF officiel
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      height: '575px',
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 3
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Cliquez sur "Prévisualiser" pour générer un aperçu PDF
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Images section */}
              <Grid item xs={12}>
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2">Images du cours</Typography>
                    <Button
                      size="small"
                      startIcon={<ImageIcon />}
                      onClick={handleOpenImageSelection}
                      disabled={!selectedCourse || (selectedCourse.is_public && !isUserAuthor(selectedCourse))}
                    >
                      Gérer les images
                    </Button>
                  </Box>
                  {selectedCourse?.library_images_details && selectedCourse.library_images_details.length > 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        overflowX: 'auto',
                        py: 1,
                        '&::-webkit-scrollbar': {
                          height: '8px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'rgba(0,0,0,0.2)',
                          borderRadius: '4px',
                        }
                      }}
                    >
                      {selectedCourse.library_images_details.map((img, index) => (
                        <Box
                          key={img.id}
                          onClick={() => handleCopyImageTag(index)}
                          sx={{
                            position: 'relative',
                            minWidth: '120px',
                            minHeight: '100px',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: 2,
                            },
                            '&:hover .delete-btn': {
                              opacity: 1,
                            }
                          }}
                        >
                          {img.thumbnail_url || img.image_file_url ? (
                            <Box
                              component="img"
                              src={img.thumbnail_url || img.image_file_url || ''}
                              alt={img.title}
                              sx={{
                                width: '120px',
                                height: '100px',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: '120px',
                                height: '100px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'action.hover',
                              }}
                            >
                              <ImageIcon />
                            </Box>
                          )}
                          {(!selectedCourse.is_public || isUserAuthor(selectedCourse)) && (
                            <IconButton
                              className="delete-btn"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveLibraryImage(img.id);
                              }}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'background.paper',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                '&:hover': {
                                  bgcolor: 'error.main',
                                  color: 'error.contrastText',
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              p: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: 'bold',
                              }}
                            >
                              &balise_image_{index + 1}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '0.65rem',
                              }}
                            >
                              {img.title}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        mt: 1,
                        p: 3,
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Aucune image ajoutée à ce cours
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Thumbnail and Tags section - side by side */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {/* Thumbnail section */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        height: '100%',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        borderColor: 'divider',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <ImageIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                          Miniature du cours
                        </Typography>
                      </Box>
                      {selectedCourse?.thumbnail_url ? (
                        <Box>
                          <Box
                            component="img"
                            src={selectedCourse.thumbnail_url}
                            alt="Miniature du cours"
                            sx={{
                              width: '100%',
                              maxHeight: '180px',
                              objectFit: 'contain',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: 'grey.50',
                              display: 'block',
                              mb: 1.5
                            }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setThumbnailSelectorOpen(true)}
                            disabled={!selectedCourse || (selectedCourse.is_public && !isUserAuthor(selectedCourse)) || !selectedCourse.preview_pdf_url}
                          >
                            Modifier
                          </Button>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            p: 3,
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            textAlign: 'center',
                            bgcolor: 'grey.50',
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Aucune miniature définie
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setThumbnailSelectorOpen(true)}
                            disabled={!selectedCourse || (selectedCourse.is_public && !isUserAuthor(selectedCourse)) || !selectedCourse.preview_pdf_url}
                            sx={{ mt: 1 }}
                          >
                            Définir une miniature
                          </Button>
                          {(!selectedCourse?.preview_pdf_url) && (
                            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
                              Générez d'abord un aperçu PDF
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* Tags section */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2.5,
                        height: '100%',
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        borderColor: 'divider',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                          }}
                        >
                          🏷️
                        </Box>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                          Tags
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          minHeight: '100px',
                          p: 2,
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 1,
                          bgcolor: 'grey.50',
                          mb: 1.5,
                        }}
                      >
                        {tags.length > 0 ? (
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {tags.map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                onDelete={(selectedCourse?.is_public && !isUserAuthor(selectedCourse)) ? undefined : () => handleRemoveTag(tag)}
                                size="small"
                                disabled={selectedCourse?.is_public && !isUserAuthor(selectedCourse)}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center' }}>
                            Aucun tag ajouté
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" gap={1}>
                        <TextField
                          size="small"
                          placeholder="Ajouter un tag"
                          value={tagInput}
                          onChange={(e) => {
                            if (selectedCourse && (!selectedCourse.is_public || isUserAuthor(selectedCourse))) {
                              setTagInput(e.target.value);
                            }
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                          disabled={!selectedCourse || (selectedCourse.is_public && !isUserAuthor(selectedCourse))}
                          InputProps={{
                            readOnly: selectedCourse?.is_public && !isUserAuthor(selectedCourse)
                          }}
                          sx={{
                            flex: 1,
                            '& .MuiInputBase-input.Mui-disabled': {
                              backgroundColor: '#f5f5f5',
                              WebkitTextFillColor: '#666',
                            }
                          }}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleAddTag}
                          disabled={!selectedCourse || (selectedCourse.is_public && !isUserAuthor(selectedCourse))}
                        >
                          Ajouter
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              {selectedCourse?.is_public && !isUserAuthor(selectedCourse) && (
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<DuplicateIcon />}
                    onClick={handleDuplicateCourse}
                    disabled={!selectedCourse || loading}
                  >
                    Dupliquer
                  </Button>
                </Grid>
              )}
            </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Course/Container Creation Dialog */}
      {courseDialogOpen && (
        <Dialog open={courseDialogOpen} onClose={() => setCourseDialogOpen(false)}>
          <DialogTitle>Nouveau</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup
                value={createType}
                exclusive
                onChange={(e, newValue) => newValue && setCreateType(newValue)}
                fullWidth
              >
                <ToggleButton value="container">
                  <FolderIcon sx={{ mr: 1 }} />
                  Contenant
                </ToggleButton>
                <ToggleButton value="course">
                  <FileIcon sx={{ mr: 1 }} />
                  Nouveau cours
                </ToggleButton>
                <ToggleButton value="existing">
                  <DuplicateIcon sx={{ mr: 1 }} />
                  Cours existant
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {createType === 'container' && (
              <>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Nom du contenant"
                  fullWidth
                  variant="outlined"
                  value={containerTitle}
                  onChange={(e) => setContainerTitle(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateContainer();
                    }
                  }}
                />
                <TextField
                  margin="dense"
                  label="Description (optionnel)"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={2}
                  value={containerDescription}
                  onChange={(e) => setContainerDescription(e.target.value)}
                />
              </>
            )}
            {createType === 'course' && (
              <TextField
                autoFocus
                margin="dense"
                label="Nom du cours"
                fullWidth
                variant="outlined"
                value={newCourseTitle}
                onChange={(e) => setNewCourseTitle(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newCourseTitle.trim()) {
                    handleCreateNewCourse();
                  }
                }}
              />
            )}
            {createType === 'existing' && (
              <Box sx={{ mt: 2, minHeight: 300, maxHeight: 400, overflow: 'auto' }}>
                <CourseChoiceContent
                  onSelect={(course) => {
                    handleCourseChoiceSelect(course);
                    setCourseDialogOpen(false);
                    setCreateType('course');
                  }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setCourseDialogOpen(false);
              setContainerTitle('');
              setContainerDescription('');
              setNewCourseTitle('');
              setCreateType('course');
            }}>
              Annuler
            </Button>
            {createType === 'container' && (
              <Button onClick={handleCreateContainer} variant="contained" color="primary">
                Créer le contenant
              </Button>
            )}
            {createType === 'course' && (
              <Button
                onClick={() => {
                  console.log('Button clicked, calling handleCreateNewCourse');
                  handleCreateNewCourse();
                }}
                variant="contained"
                color="primary"
                disabled={!newCourseTitle.trim()}
              >
                Créer le cours
              </Button>
            )}
            {createType === 'existing' && (
              <Button onClick={() => {
                setCourseDialogOpen(false);
                setCreateType('course');
              }}>
                Fermer
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}


      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Supprimer le cours"
        itemName={courseToDelete?.title || ''}
        isPublic={courseToDelete?.is_public || false}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setCourseToDelete(null);
        }}
      />

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Renommer {itemToRename?.isContainer ? 'le contenant' : 'le cours'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nouveau titre"
            fullWidth
            variant="outlined"
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          {itemToRename?.isContainer && (
            <>
              <TextField
                margin="dense"
                label="Description (optionnel)"
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                value={editContainerDescription}
                onChange={(e) => setEditContainerDescription(e.target.value)}
                sx={{ mb: 2 }}
              />
              <ThumbnailPicker
                value={editContainerThumbnail}
                onChange={setEditContainerThumbnail}
                currentThumbnailUrl={(itemToRename?.item as any)?.thumbnail_url}
              />
            </>
          )}
          {itemToRename && !itemToRename.isContainer && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="caption" display="block" color="textSecondary">
                Date de création: {new Date((itemToRename.item as Course).created_at).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
              <Typography variant="caption" display="block" color="textSecondary">
                Dernière modification: {new Date((itemToRename.item as Course).updated_at).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRenameDialogOpen(false); setEditContainerDescription(''); }}>Annuler</Button>
          <Button
            onClick={handleConfirmRename}
            variant="contained"
            disabled={!renameTitle.trim()}
          >
            Renommer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Selection Dialog */}
      <Dialog
        open={imageSelectionDialogOpen}
        onClose={() => setImageSelectionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Sélectionner des images</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {allImages.map((image) => {
              const isAdded = selectedCourse?.library_images?.includes(image.id);
              return (
                <Grid item xs={6} sm={4} md={3} key={image.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: isAdded ? '2px solid' : '1px solid',
                      borderColor: isAdded ? 'primary.main' : 'divider',
                      position: 'relative',
                    }}
                    onClick={() => {
                      if (isAdded) {
                        handleRemoveLibraryImage(image.id);
                      } else {
                        handleAddLibraryImage(image.id);
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 120,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.hover',
                      }}
                    >
                      {image.thumbnail_url || image.image_file_url ? (
                        <Box
                          component="img"
                          src={image.thumbnail_url || image.image_file_url || ''}
                          alt={image.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <ImageIcon sx={{ fontSize: 40 }} />
                      )}
                    </Box>
                    <CardContent sx={{ p: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {image.title}
                      </Typography>
                    </CardContent>
                    {isAdded && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 16,
                        }}
                      >
                        ✓
                      </Box>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {allImages.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              Aucune image disponible
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageSelectionDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Thumbnail Selector */}
      {selectedCourse?.preview_pdf_url && (
        <ThumbnailSelector
          open={thumbnailSelectorOpen}
          onClose={() => setThumbnailSelectorOpen(false)}
          pdfUrl={selectedCourse.preview_pdf_url}
          onConfirm={handleSetThumbnail}
        />
      )}

      {/* AI Assistant Dialog */}
      <Dialog
        open={aiAssistantOpen}
        onClose={() => setAiAssistantOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AiIcon />
            <Typography variant="h6">Assistant IA LaTeX</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            {/* Zone de prompt */}
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" height="100%">
                <Typography variant="subtitle2" gutterBottom>
                  Prompt pour l'IA
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="Décrivez les modifications que vous souhaitez apporter au code LaTeX..."
                  variant="outlined"
                  sx={{
                    flex: 1,
                    '& .MuiInputBase-root': {
                      height: '100%',
                      alignItems: 'flex-start',
                    },
                    '& textarea': {
                      height: '100% !important',
                      overflow: 'auto !important',
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAiAssist}
                  disabled={aiLoading || !aiPrompt.trim()}
                  sx={{ mt: 2 }}
                  startIcon={aiLoading ? <CircularProgress size={20} /> : <AiIcon />}
                >
                  {aiLoading ? 'Génération...' : 'Générer avec IA'}
                </Button>
              </Box>
            </Grid>

            {/* Zone de contenu LaTeX */}
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" height="100%">
                <Typography variant="subtitle2" gutterBottom>
                  Contenu LaTeX
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  value={aiLatexContent}
                  onChange={(e) => setAiLatexContent(e.target.value)}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    fontFamily: 'monospace',
                    '& .MuiInputBase-root': {
                      height: '100%',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '0.875rem',
                    },
                    '& textarea': {
                      height: '100% !important',
                      overflow: 'auto !important',
                    }
                  }}
                />
              </Box>
            </Grid>

            {/* Zone d'aperçu PDF */}
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" height="100%">
                <Typography variant="subtitle2" gutterBottom>
                  Aperçu PDF
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    flex: 1,
                    p: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {aiLatexContent ? (
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                      <SimplePdfViewer
                        key={aiPdfKey}
                        courseId={selectedCourse?.id || 0}
                        latexContent={aiLatexContent}
                        onRetryWithAi={handleRetryWithAi}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Aucun contenu LaTeX à prévisualiser
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiAssistantOpen(false)}>
            Annuler
          </Button>
          <Button onClick={handleApplyAiContent} variant="contained" color="primary">
            Appliquer au cours
          </Button>
        </DialogActions>
      </Dialog>

      {/* LaTeX Help Dialog */}
      <Dialog
        open={latexHelpOpen}
        onClose={() => setLatexHelpOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <HelpIcon />
            <Typography variant="h6">Aide LaTeX - Commandes courantes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Structure du document */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Structure du document
              </Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <pre style={{ margin: 0 }}>
{`\\documentclass{article}
\\usepackage[utf8]{inputenc}
\\usepackage[T1]{fontenc}
\\usepackage[french]{babel}

\\begin{document}
  Votre contenu ici
\\end{document}`}
                </pre>
              </Box>
            </Box>

            {/* Sections et titres */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Sections et titres
              </Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <pre style={{ margin: 0 }}>
{`\\section{Titre de section}
\\subsection{Sous-titre}
\\subsubsection{Sous-sous-titre}
\\paragraph{Paragraphe}
\\subparagraph{Sous-paragraphe}`}
                </pre>
              </Box>
            </Box>

            {/* Mise en forme du texte */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Mise en forme du texte
              </Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <pre style={{ margin: 0 }}>
{`\\textbf{Texte en gras}
\\textit{Texte en italique}
\\underline{Texte souligné}
\\texttt{Police monospace}
\\emph{Texte mis en emphase}`}
                </pre>
              </Box>
            </Box>

            {/* Listes */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Listes
              </Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <pre style={{ margin: 0 }}>
{`\\begin{itemize}
  \\item Premier élément
  \\item Deuxième élément
\\end{itemize}

\\begin{enumerate}
  \\item Premier élément numéroté
  \\item Deuxième élément
\\end{enumerate}`}
                </pre>
              </Box>
            </Box>

            {/* Mathématiques */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Mathématiques
              </Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <pre style={{ margin: 0 }}>
{`Formule en ligne : $E = mc^2$

Formule centrée :
\\[ \\int_a^b f(x) dx \\]

Environnement équation :
\\begin{equation}
  \\sum_{i=1}^n i = \\frac{n(n+1)}{2}
\\end{equation}

Fractions : \\frac{a}{b}
Racine carrée : \\sqrt{x}
Exposant : x^2
Indice : x_i`}
                </pre>
              </Box>
            </Box>

            {/* Tableaux */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Tableaux
              </Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <pre style={{ margin: 0 }}>
{`\\begin{tabular}{|c|c|c|}
  \\hline
  Colonne 1 & Colonne 2 & Colonne 3 \\\\
  \\hline
  A & B & C \\\\
  D & E & F \\\\
  \\hline
\\end{tabular}`}
                </pre>
              </Box>
            </Box>

            {/* Figures et images */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Figures et images
              </Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <pre style={{ margin: 0 }}>
{`\\begin{figure}[h]
  \\centering
  \\includegraphics[width=0.5\\textwidth]{image.png}
  \\caption{Légende de l'image}
  \\label{fig:mon_image}
\\end{figure}`}
                </pre>
              </Box>
            </Box>

            {/* Caractères spéciaux */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Caractères spéciaux
              </Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <pre style={{ margin: 0 }}>
{`\\% (pourcentage)
\\$ (dollar)
\\& (esperluette)
\\_ (underscore)
\\{ \\} (accolades)
\\textbackslash (backslash)
\\~{} (tilde)
\\^{} (circonflexe)`}
                </pre>
              </Box>
            </Box>

            {/* Espacements */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">
                Espacements
              </Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <pre style={{ margin: 0 }}>
{`\\\\ (saut de ligne)
\\newpage (nouvelle page)
\\vspace{1cm} (espace vertical)
\\hspace{2cm} (espace horizontal)
~ (espace insécable)`}
                </pre>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLatexHelpOpen(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen LaTeX Editor Dialog */}
      <Dialog
        open={fullscreenLatexEditorOpen}
        onClose={() => setFullscreenLatexEditorOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            height: '90vh',
            width: '95vw',
            maxWidth: '95vw'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FullscreenIcon />
            <Typography variant="h6">Éditeur LaTeX - Plein Écran</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{
              display: 'flex',
              height: '100%',
              gap: 0,
              overflow: 'hidden'
            }}
          >
            {/* LaTeX Editor */}
            <Box
              sx={{
                width: `${editorWidth}%`,
                display: 'flex',
                flexDirection: 'column',
                minWidth: '20%',
                maxWidth: '80%'
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Contenu LaTeX
              </Typography>
              <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <LatexEditor
                  value={latexContent}
                  onChange={(newValue) => {
                    if (selectedCourse && (!selectedCourse.is_public || isUserAuthor(selectedCourse))) {
                      setLatexContent(newValue);
                    }
                  }}
                  height="calc(90vh - 180px)"
                  disabled={!selectedCourse || (selectedCourse.is_public && !isUserAuthor(selectedCourse))}
                />
              </Box>
            </Box>

            {/* Resizable Divider */}
            <Box
              onMouseDown={(e) => {
                e.preventDefault();
                const startX = e.clientX;
                const startWidth = editorWidth;
                const dialogElement = (e.currentTarget as HTMLElement).closest('[role="dialog"]') as HTMLElement;
                const dialogWidth = dialogElement?.clientWidth || 1000;

                const handleMouseMove = (moveEvent: MouseEvent) => {
                  const deltaX = moveEvent.clientX - startX;
                  const deltaPercent = (deltaX / dialogWidth) * 100;
                  const newWidth = Math.min(80, Math.max(20, startWidth + deltaPercent));
                  setEditorWidth(newWidth);
                };

                const handleMouseUp = () => {
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }}
              sx={{
                width: '4px',
                cursor: 'col-resize',
                backgroundColor: 'divider',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
                transition: 'background-color 0.2s',
                flexShrink: 0
              }}
            />

            {/* PDF Preview */}
            <Box
              sx={{
                width: `${100 - editorWidth}%`,
                display: 'flex',
                flexDirection: 'column',
                minWidth: '20%',
                maxWidth: '80%',
                pl: 2
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">
                  Aperçu PDF
                </Typography>
                <Button
                  size="small"
                  onClick={handlePreview}
                  variant="contained"
                  disabled={!selectedCourse}
                  startIcon={<PreviewIcon />}
                >
                  Générer
                </Button>
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  flexGrow: 1,
                  p: 1,
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5'
                }}
              >
                {showPdfPreview && selectedCourse ? (
                  <SimplePdfViewer
                    key={pdfPreviewKey}
                    courseId={selectedCourse.id}
                    latexContent={latexContent}
                    onRetryWithAi={handleRetryWithAi}
                  />
                ) : selectedCourse?.preview_pdf_url ? (
                  <PdfDisplay
                    url={selectedCourse.preview_pdf_url}
                    title="Aperçu du cours"
                  />
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      height: '100%',
                      p: 3
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      Cliquez sur "Générer" pour créer un aperçu PDF
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullscreenLatexEditorOpen(false)} variant="contained">
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Recap Modal */}
      <QuickRecapModal
        open={quickRecapModalOpen}
        onClose={() => setQuickRecapModalOpen(false)}
        itemTitle={quickRecapCourse?.title || ''}
        courses={courses.filter(c => c.id !== quickRecapCourse?.id)}
        videos={videos}
        selectedCourseId={quickRecapCourse?.quick_recap_course}
        selectedVideoId={quickRecapCourse?.quick_recap_video}
        onSaveCourse={handleSaveQuickRecapCourse}
        onSaveVideo={handleSaveQuickRecapVideo}
      />
    </Box>
  );
};

export default Courses;